/*
 * @Author: Auto Generated
 * @FilePath: /mall-fe/src/comps/RuntimeConfigurationProvider.tsx
 * @Description: 运行时配置 Provider
 *
 * 在客户端获取运行时配置数据，并通过 Context 提供给子组件
 * 包括 OSS、OBS、静态资源域名等配置
 */

import { ReactNode, useEffect } from 'react';
import { RuntimeConfigurationContext } from 'libs/contexts';
import { useRuntimeConfigurationQuery } from 'hooks/useRuntimeConfigurationQuery';
import { setObsHost, setOssHost, obsHost, ossHost } from 'libs/utils';

export interface RuntimeConfigurationProviderProps {
  children: ReactNode;
  /**
   * 初始数据（可选）
   * 用于服务端渲染场景，如果提供则使用初始数据，避免客户端重新请求
   */
  initialData?: any[];
}

/**
 * 运行时配置 Provider 组件
 *
 * 功能：
 * 1. 使用 React Query 在客户端获取运行时配置
 * 2. 通过 RuntimeConfigurationContext 提供数据给子组件
 * 3. 自动处理 loading 和 error 状态
 * 4. 支持初始数据，避免重复请求
 * 5. 自动设置 OSS/OBS 全局配置
 *
 * @example
 * ```tsx
 * // 在 _app.tsx 中使用
 * <RuntimeConfigurationProvider>
 *   <YourApp />
 * </RuntimeConfigurationProvider>
 *
 * // 在子组件中使用
 * function SomeComponent() {
 *   const runtimeConfig = useContext(RuntimeConfigurationContext);
 *   const ossHost = runtimeConfig?.find(item => item.key === 'oss')?.value;
 *   return <div>OSS 域名: {ossHost}</div>;
 * }
 * ```
 */
export default function RuntimeConfigurationProvider({
  children,
  initialData,
}: RuntimeConfigurationProviderProps) {
  // 使用 React Query 获取运行时配置
  const {
    data: runtimeConfiguration,
    isLoading,
    isError,
    error,
  } = useRuntimeConfigurationQuery({
    initialData,
  });

  // 错误处理：如果加载失败，使用空数组避免组件崩溃
  if (isError) {
    console.error('加载运行时配置失败:', error);
  }

  // 提供数据给子组件
  const configData = isLoading || isError ? initialData || [] : runtimeConfiguration || [];

  // 设置 OSS/OBS 全局配置
  // 当配置加载完成后，自动设置全局的 OSS/OBS 域名
  useEffect(() => {
    if (configData && configData.length > 0) {
      if (!ossHost) {
        const oss = configData.find((config) => config.key === 'oss')?.value;
        if (oss) setOssHost(oss);
      }
      if (!obsHost) {
        const obs = configData.find((config) => config.key === 'obs')?.value;
        if (obs) setObsHost(obs);
      }
    }
  }, [configData]);

  return (
    <RuntimeConfigurationContext.Provider value={configData}>
      {children}
    </RuntimeConfigurationContext.Provider>
  );
}
